<script setup>
import CategoryWiseTicket from '@/@core/components/department/CategoryWiseTicket.vue'
import EscalationWiseTicket from '@/@core/components/department/EscalationWiseTicket.vue'
import SmileyQuestionGraph from '@/@core/components/department/SmileyQuestionGraph.vue'
import TicketStatusGraph from '@/@core/components/department/TicketStatusGraph.vue'
import TicketTypesGraph from '@/@core/components/department/TicketTypesGraph.vue'
import DashboardTicketTypeCard from '@/@core/components/feedback/DashboardTicketTypeCard.vue'
import DashboardUnitOverviewCard from '@/@core/components/feedback/DashboardUnitOverviewCard.vue'
import DepartmentWiseRating from '@/@core/components/feedback/DepartmentWiseRating.vue'
import { endOfMonth, startOfMonth } from '@/@core/utils/formatters'
import { can } from '@/@layouts/plugins/casl'
import { cookieRef } from '@/@layouts/stores/config'
import TicketTable from '@/views/feedback/ticket-management/TicketTable.vue'
import { computed } from 'vue'

definePage({
  meta: {
    name: 'dashboard-department',
    subject: 'feedback.menu',
    action: 'dashboard_department',
  },
})

const unitId = ref(cookieRef('selectedUnit') ?? null)

const { data: departments } = await useApi(
  createUrl('masters/departments', {
    query: {
      select: ['id', 'name'],
    },
  }),
)

const departmentId = ref(null)

// Date utilities
const addDays = (date, days) => {
  const result = new Date(date)

  result.setDate(result.getDate() + days)

  return result
}

const addMonths = (date, months) => {
  const result = new Date(date)

  result.setMonth(result.getMonth() + months)

  return result
}

const customDateFormat = date => {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
}

const formatFloat = number => {
  if (Number(number) === number && number % 1 !== 0)
    return number.toFixed(2)

  return number
}

// Date range state
const quickDateRangeOptions = [
  {
    title: 'Today',
    value: `${customDateFormat(new Date())}`,
  },
  {
    title: 'Yesterday',
    value: `${customDateFormat(addDays(new Date(), -1))}`,
  },
  {
    title: 'Last 7 days',
    value: `${customDateFormat(addDays(new Date(), -6))} to ${customDateFormat(new Date())}`,
  },
  {
    title: 'Last 30 days',
    value: `${customDateFormat(addDays(new Date(), -30))} to ${customDateFormat(new Date())}`,
  },
  {
    title: 'This month',
    value: `${customDateFormat(startOfMonth(new Date()))} to ${customDateFormat(new Date())}`,
  },
  {
    title: 'Last month',
    value: `${customDateFormat(startOfMonth(addMonths(new Date(), -1)))} to ${customDateFormat(endOfMonth(addMonths(new Date(), -1)))}`,
  },
  {
    title: 'Custom',
    value: null,
  },
]

const quickDateRange = ref('Last 30 days')

const setQuickDateRange = value => {
  dateRange.value = value?.value
}

const dateRange = ref()

const fromDate = ref(customDateFormat(addDays(new Date(), -30)))
const toDate = ref(customDateFormat(new Date()))

const dateRangeOptions = ref({
  mode: 'range',
  maxDate: new Date(),
  dateFormat: 'Y-m-d',
  enableTime: false,
  enableSeconds: false,
  noCalendar: false,
  altInput: true,
  altFormat: 'F j, Y',
  altInputClass: 'form-control',
  altInputPlaceholder: 'Select date',
  inline: false,
  static: false,
  appendTo: document.body,
  clickOpens: true,
  onChange: (selectedDates, dateStr, instance) => {
    fromDate.value = customDateFormat(selectedDates[0])
    if (selectedDates[1])
      toDate.value = customDateFormat(selectedDates[1])
    else
      toDate.value = customDateFormat(selectedDates[0])

    dateRange.value = dateStr
  },
  defaultDate: [customDateFormat(addDays(new Date(), -30)), customDateFormat(new Date())],
})

watch(dateRange, value => {
  if (value) {
    quickDateRange.value = quickDateRangeOptions.find(option => option.value === value)?.title || 'Custom'
  }
}, { immediate: true })

const dateRangeValue = computed(() => {
  return [fromDate.value, toDate.value]
})

// API calls
const { data: totalFeedbackData } = useApi(
  createUrl('/dashboard/feedbacks/total-count', {
    query: {
      search: {
        created_at: dateRangeValue,
        unit_id: unitId,
      },
    },
  }),
)

const { data: totalTicketTypeData } = useApi(
  createUrl('/dashboard/tickets/ticket-type', {
    query: {
      search: {
        created_at: dateRangeValue,
        unit_id: unitId,
        department_id: departmentId,
      },
    },
  }),
)

const { data: departmentPSatData } = useApi(
  createUrl('/dashboard/feedbacks/p-sat/department', {
    query: {
      search: {
        unit_id: unitId,
        created_at: dateRangeValue,
      },
    },
  }),
)

// P-Sat computed
const pSatData = computed(() => {
  if (!departmentPSatData.value) return null

  if (departmentId.value) {
    const data = departmentPSatData.value.find(
      x => x.department_id == departmentId.value,
    )

    return {
      overall_p_sat: formatFloat(data?.overall_p_sat) ?? null,
      p_sat_percentage: formatFloat(data?.p_sat_percentage) ?? null,
    }
  } else {
    const data = departmentPSatData.value.reduce(
      (acc, obj) => {
        acc.overall_p_sat += obj.overall_p_sat || 0
        acc.p_sat_percentage += obj.p_sat_percentage || 0

        return acc
      },
      { overall_p_sat: 0, p_sat_percentage: 0 },
    )

    const count = departmentPSatData.value?.length > 0 ? departmentPSatData.value.length : 1

    return {
      overall_p_sat: formatFloat(data?.overall_p_sat / count) ?? null,
      p_sat_percentage: formatFloat(data?.p_sat_percentage / count) ?? 0,
    }
  }
})

// Ticket table dialog
const ticketTableFilter = ref(null)
const isTicketTableDialogVisible = ref(false)

const onUpdateTicketTableFilter = data => {
  ticketTableFilter.value = {
    created_at: [fromDate.value, toDate.value],
    unit_id: unitId.value,
    status: [],
    ticket_type: ['COMPLAINT', 'COMPLIMENT', 'SUGGESTION', 'SPECIAL_REQUEST'],
    department_id: departmentId.value,
    ...data,
  }
  isTicketTableDialogVisible.value = true
}

watch(() => isTicketTableDialogVisible.value, value => {
  if (value === false) ticketTableFilter.value = null
})
</script>

<template>
  <div>
    <div class="text-h4 mb-6">
      Department Dashboard
    </div>

    <!-- Filter Card -->
    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardText>
        <VRow align="center">
          <VCol
            cols="12"
            md="4"
            sm="6"
          >
            <AppSelect
              v-model="quickDateRange"
              label="Quick Range"
              return-object
              :item-title="item => item.title"
              :item-value="item => item.title"
              :items="quickDateRangeOptions"
              @update:model-value="setQuickDateRange"
            />
          </VCol>
          <VCol
            cols="12"
            md="4"
            sm="6"
          >
            <AppDateTimePicker
              v-model="dateRange"
              label="Range"
              placeholder="Select date"
              :config="dateRangeOptions"
            />
          </VCol>
          <VCol
            v-if="departments.length > 1"
            cols="12"
            md="4"
            sm="6"
          >
            <AppCombobox
              v-model="departmentId"
              :items="departments"
              :item-title="item => item?.name"
              :item-value="item => item?.id"
              label="Department"
              placeholder="Select By Department"
              :multiple="false"
              :return-object="false"
              :clearable="true"
            />
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Analytics Cards -->
    <VRow class="match-height">
      <!-- Feedback Overview + P-Sat -->
      <VCol
        cols="12"
        md="8"
        style="animation-delay: 0ms;"
      >
        <DashboardUnitOverviewCard
          :feedback-data="totalFeedbackData"
          :p-sat-data="pSatData"
        />
      </VCol>

      <!-- Tickets By Type -->
      <VCol
        cols="12"
        md="4"
        style="animation-delay: 70ms;"
      >
        <DashboardTicketTypeCard :data="totalTicketTypeData" />
      </VCol>

      <!-- Department Chart Widgets -->
      <VCol
        v-if="can('view', 'department_dashboard.widget.department_wise_rating')"
        cols="12"
      >
        <AppLazy :key="unitId + departmentId + fromDate + toDate">
          <DepartmentWiseRating
            :from="fromDate"
            :to="toDate"
            :unit-id="unitId"
            :department-id="departmentId"
          />
        </AppLazy>
      </VCol>

      <VCol
        v-if="can('view', 'department_dashboard.widget.ticket_status')"
        cols="12"
      >
        <AppLazy :key="unitId + departmentId + fromDate + toDate">
          <TicketStatusGraph
            :from="fromDate"
            :to="toDate"
            :unit-id="unitId"
            :department-id="departmentId"
            @update:ticket-table-filter="onUpdateTicketTableFilter"
          />
        </AppLazy>
      </VCol>

      <VCol
        v-if="can('view', 'department_dashboard.widget.ticket_status')"
        cols="12"
      >
        <AppLazy :key="unitId + departmentId + fromDate + toDate">
          <TicketTypesGraph
            :from="fromDate"
            :to="toDate"
            :unit-id="unitId"
            :department-id="departmentId"
          />
        </AppLazy>
      </VCol>

      <VCol
        v-if="can('view', 'department_dashboard.widget.category_wise_ticket', 'feedback')"
        cols="12"
      >
        <AppLazy :key="unitId + departmentId + fromDate + toDate">
          <CategoryWiseTicket
            :from="fromDate"
            :to="toDate"
            :unit-id="unitId"
            :department-id="departmentId"
            @update:ticket-table-filter="onUpdateTicketTableFilter"
          />
        </AppLazy>
      </VCol>

      <VCol
        v-if="can('view', 'department_dashboard.widget.escalation_wise_ticket', 'feedback')"
        cols="12"
      >
        <AppLazy :key="unitId + departmentId + fromDate + toDate">
          <EscalationWiseTicket
            :from="fromDate"
            :to="toDate"
            :unit-id="unitId"
            :department-id="departmentId"
            @update:ticket-table-filter="onUpdateTicketTableFilter"
          />
        </AppLazy>
      </VCol>

      <VCol
        v-if="departmentId"
        cols="12"
      >
        <AppLazy :key="unitId + departmentId + fromDate + toDate + 'Smiley'">
          <SmileyQuestionGraph
            :from="fromDate"
            :to="toDate"
            :unit-id="unitId"
            :department-id="departmentId"
          />
        </AppLazy>
      </VCol>
    </VRow>
  </div>

  <!-- Ticket Table Dialog -->
  <VDialog v-model="isTicketTableDialogVisible">
    <DialogCloseBtn
      size="small"
      @click="() => (isTicketTableDialogVisible = !isTicketTableDialogVisible)"
    />

    <VCard>
      <VCardText>
        <TicketTable
          ref="refTicketTable"
          page="ticket-management"
          :ticket-filters="ticketTableFilter"
          :action-visible="false"
          :style="{
            cardVariant: 'flat',
          }"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>
